import { useMemo, useState, useCallback, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { decodeInvoice, InvoiceData } from '@/lib/invoice';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { fiatToBCH, formatBCH, formatFiat, generateBCHUri } from '@/lib/bch';
import { QRCodeCanvas } from '@/components/ui/qrcode';

function formatDate(ts: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Payment detected state ──────────────────────────────────────────────────

function PaidState({ data, txid, bchAmount }: { data: InvoiceData; txid: string; bchAmount: number }) {
  const explorerUrl = `https://bchexplorer.cash/tx/${txid}`;
  return (
    <div className="space-y-5">
      {/* Paid badge */}
      <div className="flex flex-col items-center gap-3 py-2">
        <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: data.brandColor ?? '#0AC18E' }}>
          <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-3xl font-black text-zinc-900">PAID</h2>
        <p className="text-sm text-zinc-400">Payment confirmed on the Bitcoin Cash network</p>
      </div>

      {/* Amount */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5 text-center shadow-sm">
        <p className="text-3xl font-black text-zinc-900">{formatFiat(data.total, data.currency)}</p>
        <p className="text-sm font-mono font-medium mt-1" style={{ color: data.brandColor ?? '#0AC18E' }}>
          {formatBCH(bchAmount)} BCH
        </p>
      </div>

      {/* Transaction link */}
      <a
        href={explorerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-zinc-50 border border-zinc-200 text-sm font-semibold hover:bg-zinc-100 transition-colors"
        style={{ color: data.brandColor ?? '#0AC18E' }}
      >
        <span>🔍</span> Verify on Block Explorer
      </a>
    </div>
  );
}

// ─── Main invoice card ───────────────────────────────────────────────────────

function InvoiceCard({ data }: { data: InvoiceData }) {
  const { data: priceData } = useBCHPrice(data.currency);
  const [paid, setPaid] = useState(false);
  const [paidTxid, setPaidTxid] = useState('');
  const [paidBCH, setPaidBCH] = useState(0);
  const [copied, setCopied] = useState(false);

  const bchAmount = priceData ? fiatToBCH(data.total, priceData.price) : 0;
  const bchUri = bchAmount > 0 ? generateBCHUri(data.address, bchAmount, `Invoice ${data.id.slice(0, 8)}`) : '';
  const qrSize = Math.min(window.innerWidth - 80, 280);

  const isOverdue = data.dueDate > 0 && data.dueDate < Date.now();

  const handleDetected = useCallback(({ txid, amount }: { txid: string; amount: number }) => {
    setPaidTxid(txid);
    setPaidBCH(amount);
    setPaid(true);
  }, []);

  const { reset } = usePaymentDetection(
    data.address,
    bchAmount,
    !paid && bchAmount > 0,
    handleDetected,
  );

  useEffect(() => {
    return () => reset();
  }, [reset]);

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-zinc-100">
        {/* Header */}
        <div className="px-6 py-6 text-center" style={{ backgroundColor: data.brandColor ?? '#0AC18E' }}>
          {data.logoDataUrl ? (
            <div className="flex justify-center mb-3">
              <img src={data.logoDataUrl} alt={data.merchantName} className="h-14 max-w-[180px] object-contain" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-black text-sm">INV</span>
            </div>
          )}
          <h1 className="text-white font-black text-xl">{data.merchantName}</h1>
          {data.merchantTagline && (
            <p className="text-white/70 text-sm mt-0.5">{data.merchantTagline}</p>
          )}
        </div>

        {/* Invoice badge */}
        <div className="flex justify-center -mt-4 mb-3">
          <span className="bg-white text-xs font-black px-4 py-1 rounded-full shadow-sm tracking-widest uppercase border-2" style={{ color: data.brandColor ?? '#0AC18E', borderColor: data.brandColor ?? '#0AC18E' }}>
            {paid ? '✓ Paid' : 'Invoice'}
          </span>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Invoice meta */}
          <div className="flex justify-between text-xs text-zinc-400">
            <span>Invoice #{data.id.slice(0, 8).toUpperCase()}</span>
            <span>{formatDateTime(data.createdAt)}</span>
          </div>

          {/* Bill to */}
          <div className="bg-zinc-50 rounded-xl px-4 py-3 border border-zinc-100">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Bill to</p>
            <p className="text-sm font-semibold text-zinc-800">{data.clientName}</p>
            {data.clientEmail && <p className="text-xs text-zinc-400">{data.clientEmail}</p>}
          </div>

          {/* Due date */}
          {data.dueDate > 0 && (
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-semibold ${
              isOverdue && !paid
                ? 'bg-red-50 border-red-200 text-red-600'
                : 'bg-zinc-50 border-zinc-100 text-zinc-500'
            }`}>
              <span>{isOverdue && !paid ? '⚠️' : '📅'}</span>
              <span>Due: {formatDate(data.dueDate)}</span>
              {isOverdue && !paid && <span className="ml-auto text-red-500 font-bold">Overdue</span>}
            </div>
          )}

          {/* Line items */}
          {data.items.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Items</p>
              <div className="divide-y divide-zinc-100">
                {data.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm text-zinc-500 shrink-0">{item.qty}×</span>
                      <span className="text-sm font-medium text-zinc-800 truncate">{item.description}</span>
                    </div>
                    <span className="text-sm font-semibold text-zinc-700 shrink-0 ml-3">
                      {formatFiat(item.price * item.qty, data.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t-2 border-dashed border-zinc-100" />

          {/* Note */}
          {data.note && (
            <div className="bg-zinc-50 rounded-xl px-3 py-2.5 border border-zinc-100">
              <p className="text-xs text-zinc-500 font-medium">📝 {data.note}</p>
            </div>
          )}

          {/* Totals */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm text-zinc-500">
              <span>Subtotal</span>
              <span>{formatFiat(data.subtotal, data.currency)}</span>
            </div>
            {data.taxRate > 0 && (
              <div className="flex justify-between text-sm text-zinc-500">
                <span>{data.taxName} ({data.taxRate}%)</span>
                <span>{formatFiat(data.taxAmount, data.currency)}</span>
              </div>
            )}
            <div className="flex items-end justify-between pt-2 border-t border-zinc-100">
              <span className="text-sm font-semibold text-zinc-500">Total Due</span>
              <p className="text-3xl font-black text-zinc-900">
                {formatFiat(data.total, data.currency)}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-dashed border-zinc-100" />

          {/* Payment section */}
          {paid ? (
            <PaidState data={data} txid={paidTxid} bchAmount={paidBCH} />
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-bold text-zinc-700 text-center">Pay with Bitcoin Cash</p>

              {/* QR */}
              {bchAmount > 0 ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100">
                    <QRCodeCanvas value={bchUri} size={qrSize} level="M" className="rounded-xl" />
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-black text-zinc-900">{formatBCH(bchAmount)} BCH</p>
                    {priceData && (
                      <p className="text-xs text-zinc-400 mt-0.5">
                        1 BCH = {formatFiat(priceData.price, data.currency)}
                        {priceData.stale && <span className="text-amber-500 ml-1">(cached)</span>}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-zinc-400 animate-pulse">Loading BCH price...</p>
                </div>
              )}

              {/* Address */}
              <div className="bg-zinc-50 rounded-xl p-3 border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Send to</p>
                <p className="text-xs font-mono text-zinc-600 break-all leading-relaxed">
                  {data.address}
                </p>
              </div>

              {/* Waiting indicator */}
              {bchAmount > 0 && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: data.brandColor ?? '#0AC18E' }} />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: data.brandColor ?? '#0AC18E' }} />
                  </span>
                  <span className="text-sm text-zinc-400">Waiting for payment...</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-zinc-50 border-t border-zinc-100 px-6 py-4 text-center">
          <p className="text-xs text-zinc-400">
            Pay with Bitcoin Cash •{' '}
            <a href="https://bchpay.cash" target="_blank" rel="noopener noreferrer" className="text-[#0AC18E] hover:underline">
              Powered by BCHPAY.Cash
            </a>
          </p>
        </div>
      </div>

      {/* Share / copy buttons */}
      <div className="no-print flex flex-col sm:flex-row gap-3 mt-4 w-full max-w-sm mx-auto">
        <button
          onClick={() => window.print()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-all active:scale-95"
          style={{ backgroundColor: data.brandColor ?? '#0AC18E' }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
            } catch { /* ignore */ }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border text-sm font-semibold active:scale-95 transition-all ${
            copied
              ? 'bg-[#0AC18E]/10 border-[#0AC18E] text-[#0AC18E]'
              : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
          }`}
        >
          {copied ? '✓ Copied!' : '📋 Copy Link'}
        </button>
      </div>
    </div>
  );
}

// ─── Invalid state ───────────────────────────────────────────────────────────

function InvalidInvoice() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center gap-4">
      <div className="text-5xl">❌</div>
      <h1 className="text-xl font-bold text-zinc-800">Invalid Invoice</h1>
      <p className="text-sm text-zinc-400 max-w-xs">
        This invoice link is invalid or has been corrupted. Please ask the sender to share it again.
      </p>
      <Link
        to="/"
        className="mt-2 px-6 py-3 rounded-2xl bg-[#0AC18E] text-white font-bold text-sm shadow-lg shadow-[#0AC18E]/30"
      >
        Go to BCHPAY.Cash
      </Link>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function InvoicePage() {
  const { hash } = useLocation();

  const data = useMemo(() => {
    if (!hash || hash === '#') return null;
    return decodeInvoice(hash);
  }, [hash]);

  useSeoMeta({
    title: data ? `Invoice — ${data.merchantName}` : 'Invoice',
    description: data
      ? `Invoice from ${data.merchantName} for ${formatFiat(data.total, data.currency)}`
      : 'BCH payment invoice',
  });

  if (!data) return <InvalidInvoice />;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-start py-8 px-4">
      <InvoiceCard data={data} />
    </div>
  );
}
